import { CafeListItemViewModel, IErrorResponse } from "@dining/api-client";
import { AppProps, BuildingCafeList, BuildingSortOption, StateSlice } from "../..";

const groupCafesByBuilding = (cafes: CafeListItemViewModel[]) => {
  const cafesByBuilding: BuildingCafeList[] = [];
  cafes.forEach((cafe: CafeListItemViewModel) => {
    const buildingId = cafe.buildingId || "";
    const buildingIndex = cafesByBuilding.findIndex((building) => building.id === buildingId);
    buildingIndex === -1
      ? cafesByBuilding.push({
          name: cafe.buildingName.trim(),
          id: buildingId,
          cafeList: [cafe],
        })
      : cafesByBuilding[buildingIndex].cafeList.push(cafe);
  });
  return cafesByBuilding;
};

export const sortCafesForEachBuilding = (cafesByBuilding: BuildingCafeList[]) => {
  cafesByBuilding?.forEach((building: BuildingCafeList) => {
    building?.cafeList?.sort((a, b) => a?.name?.localeCompare(b?.name));
  });

  return cafesByBuilding;
};

const sortBuildingsByOption = (option: BuildingSortOption, buildings: BuildingCafeList[]) => {
  switch (option) {
    case "Nearest":
      // TODO: Need to make dining service call and pass coordinates of user's current location.
      return buildings;
    case "A to Z":
      return buildings.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
    case "Z to A":
      return buildings.sort(
        (a, b) => -1 * a.name.localeCompare(b.name, undefined, { numeric: true }),
      );
    default:
      return buildings || [];
  }
};

export type BuildingsProps = {
  buildings: {
    loading: boolean;
    values: BuildingCafeList[];
    error?: string | IErrorResponse;
    sortedBy: BuildingSortOption;
  };
};

export type BuildingsState = BuildingsProps & {
  buildings: {
    setLoading: (loading: boolean) => void;
    setValues: (values: BuildingCafeList[]) => void;
    sort: (option: BuildingSortOption) => void;
    fetch: () => Promise<void>;
  };
};

export const defaultBuildingsProps: BuildingsProps = {
  buildings: {
    loading: false,
    values: [] as BuildingCafeList[],
    sortedBy: "A to Z",
  },
};

export const createBuildingsState: (props?: Partial<AppProps>) => StateSlice<BuildingsState> =
  (props) => (set, get) => ({
    buildings: {
      ...defaultBuildingsProps.buildings,
      ...props?.buildings,
      setLoading: (loading: boolean) =>
        set(
          (state) => ({
            ...state,
            buildings: {
              ...state.buildings,
              loading,
            },
          }),
          false,
          {
            type: "buildings.setLoading",
            loading,
          },
        ),
      setValues: (values: BuildingCafeList[]) =>
        set(
          (state) => ({
            ...state,
            buildings: {
              ...state.buildings,
              values,
            },
          }),
          false,
          {
            type: "buildings.setValues",
            values,
          },
        ),
      sort: (option: BuildingSortOption) => {
        const { buildings } = get();
        const { values } = buildings;
        const sortedValues = sortBuildingsByOption(option, values);
        set(
          (state) => ({
            ...state,
            buildings: {
              ...state.buildings,
              sortedBy: option,
              values: sortedValues,
            },
          }),
          false,
          {
            type: "buildings.sort",
            option,
          },
        );
      },
      fetch: async () => {
        const { client, buildings } = get();
        const { setLoading, setValues, sortedBy } = buildings;
        try {
          setLoading(true);
          const response = await client?.getCafesList();
          if (response?.items) {
            const values = groupCafesByBuilding(response.items);
            if (sortedBy) {
              const sortedValues = sortBuildingsByOption(sortedBy, values);
              const sortedValuesWithSortedCafes = sortCafesForEachBuilding(sortedValues);
              setValues(sortedValuesWithSortedCafes);
            } else {
              setValues(values);
            }
          }
        } catch (error) {
          // deal with error
        } finally {
          setLoading(false);
        }
      },
    },
  });
